// src/components/core/entity/EntityChannelsCard/ContactPersonModal/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { UserOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import appService from 'services/appService';
import entitiesService from 'services/entitiesService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import { generateMongoObjectId, returnOnlyNumbers } from 'utils/helpers';
import { getPhoneMeta } from 'utils/phone';
import contactPersonSearchStep from './Step1';
import contactPersonDataStep from './Step2';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ContactPersonModal = ({
  show,
  setShow,
  afterClose,
  entityId,
  existingContacts,
  onSuccess,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(
    () => [contactPersonSearchStep(), contactPersonDataStep()],
    []
  );

  const initialValues = useMemo(
    () => ({
      _searchResult: null,
      _entityFound: false,
    }),
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form }) => {
    try {
      const values = form.getFieldsValue(true);
      const entityFound = values._entityFound;
      const searchResult = values._searchResult;

      // ── MODE A: Match found - link existing entity ──────────────────────
      if (entityFound && searchResult?._id) {
        // Check for duplicate link
        const alreadyLinked = existingContacts?.some(
          c => c.linkedEntityId === searchResult._id
        );
        if (alreadyLinked) {
          appService.message({ flag: 'w', text: 'entity_already_linked' });
          return false;
        }

        await appService.message({ flag: 'i', text: 'saving_data', key: 'savePerson' });

        const contactData = {
          _id: generateMongoObjectId(),
          contactType: 'person',
          value: searchResult.name,
          linkedEntityId: searchResult._id,
          note: (values.note || '').trim(),
          countryCode: 'BR',
          isPrimary: !!values.isPrimary,
        };

        await entitiesService.addToSet({
          _id: entityId,
          operator: '$push',
          path: 'contacts',
          data: contactData,
        });

        await appService.message({ flag: 's', text: 'channel_added', key: 'savePerson' });
        onSuccess?.(searchResult, contactData);
        return true;
      }

      // ── MODE B: Not found - create new entity + link ───────────────────
      // Validate required name
      if (!values.personName?.trim()) {
        appService.message({ flag: 'w', text: 'field_required' });
        return false;
      }

      await appService.message({ flag: 'i', text: 'saving_data', key: 'savePerson' });

      // Build new entity data (send fullName so decorator splits into parts)
      const trimmedName = values.personName.trim();
      const newEntityData = {
        name: trimmedName,
        fullName: trimmedName,
        entityType: 'person',
        entityProfile: ['contact'],
        _metadata: { origin: 0 },
      };

      // Build contacts array for the new entity
      // SimpleEmail/SimplePhone use name={['field', 'value']} so values are nested objects
      const entityContacts = [];
      const rawEmail = values.personEmail;
      const emailValue = typeof rawEmail === 'object' ? rawEmail?.value : rawEmail;
      if (emailValue?.trim()) {
        entityContacts.push({
          contactType: 'email',
          value: emailValue.trim(),
          countryCode: 'BR',
          isPrimary: true,
        });
      }

      const rawPhone = values.personPhone;
      const phoneValue = typeof rawPhone === 'object' ? rawPhone?.value : rawPhone;
      const phoneCountryCode = (typeof rawPhone === 'object' ? rawPhone?.countryCode : null) || 'BR';
      if (phoneValue) {
        const cleanPhone = returnOnlyNumbers(phoneValue);
        // Derive phoneType from number using getPhoneMeta (returns 'MOBILE', 'FIXED_LINE', etc.)
        const phoneMeta = getPhoneMeta(cleanPhone, phoneCountryCode);
        const phoneType = phoneMeta?.isMobile ? 'mobile' : phoneMeta?.isLandline ? 'landline' : 'mobile';
        entityContacts.push({
          contactType: 'phone',
          value: cleanPhone,
          countryCode: phoneCountryCode,
          phoneType,
          isPrimary: !entityContacts.length,
        });
      }

      if (entityContacts.length) newEntityData.contacts = entityContacts;
      if (values.personBirthdate) newEntityData.birthdate = values.personBirthdate;

      // Create entity via API
      const url = API.ENTITIES.URL.replace(':_id?', '');
      const [savedEntity] = await axiosService.post({
        url,
        data: newEntityData,
        headers: { 'x-resultasobject': true },
      });

      if (!savedEntity?._id) {
        await appService.message({ flag: 'e', text: 'error_saving', key: 'savePerson' });
        return false;
      }

      // Link the new entity to parent
      const contactData = {
        _id: generateMongoObjectId(),
        contactType: 'person',
        value: savedEntity.name || values.personName.trim(),
        linkedEntityId: savedEntity._id,
        note: (values.note || '').trim(),
        countryCode: 'BR',
        isPrimary: !!values.isPrimary,
      };

      await entitiesService.addToSet({
        _id: entityId,
        operator: '$push',
        path: 'contacts',
        data: contactData,
      });

      await appService.message({ flag: 's', text: 'channel_added', key: 'savePerson' });
      onSuccess?.(savedEntity, contactData);
      return true;
    } catch (error) {
      await appService.message({ flag: 'e', text: 'error_saving', key: 'savePerson' });
      return false;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      afterClose={afterClose}
      icon={<UserOutlined />}
      title="contact_person"
      steps={steps}
      initialValues={initialValues}
      onFinish={handleFinish}
      width={500}
      maskClosable={false}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ContactPersonModal;
